// Vendor Imports
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import extend from 'lodash/extend';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import set from 'lodash/set';
import $ from 'jquery';
import {
  Column,
  ColumnState,
  IServerSideGetRowsRequest,
  IServerSideGetRowsParams,
  IServerSideDatasource,
} from '@ag-grid-community/core';

import {
  CustomSortConfig,
  Hierarchy,
  HierarchyColumnConfig,
  OrderConfig,
  Vif,
} from 'common/visualizations/vif';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';

import {
  setInlineDataQuery,
  setRawSoqlDataQuery,
  setAgSoqlDataQuery,
  setSocrataViewDataQuery,
} from 'common/visualizations/helpers/TableDataHelpers';
import { setAgSoqlExportQuery } from './helpers/TableDataExportHelpers';
import { setGrandTotalDataQuery } from './helpers/TableDataGrandTotalHelpers';
import { setAgUniqueValuesQuery } from './helpers/TableDataUniqueValuesHelpers';
import {
  addPivotResultCols,
  clearPivotResultCols,
  doClientSidePivot,
  isPivotInAgRequest,
  isPivotFeatureFlagOn,
  removeSortingFromNonRowGroupColumns,
  sortHierarchyColumnConfigurations,
  extractPivotColumnOrder,
  extractPivotColumnWidths,
  applyPivotColumnWidths,
  isPivotInHierarchy,
} from 'common/visualizations/helpers/PivotHelpers';
import { VisualizationOptions } from './views/BaseVisualization/types';
import AgTable from './views/AgTable';
import { Filters } from 'common/components/FilterBar/types';

import { ViewColumn } from 'common/types/viewColumn';
import { GROUP_COLUMN_PREFIX } from 'common/visualizations/views/agGridReact/Constants';
import {
  getTableHierarchies,
  getParameterOverrides,
  isCustomSortEnabled,
  isTableAdvancedTotalingEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { AgColumnFilter } from 'common/types/agGrid/filters';
import eventBus from './views/agGridReact/helpers/EventBus';
import { UniqueValuesData } from './views/agGridReact/types';
import { getCurrentHierarchyIndex } from './helpers/VifHelpers';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import {
  findHierarchyInfo,
  getActiveHierarchyId,
  getCurrentHierarchyInfo,
} from './helpers/AgTableVifHelpers';
import { AgTableRenderState, SocrataAgTable, VifEvent } from './types/agTable';
import {
  activeHierarchyIdPath,
  columnConfigurationPath,
  columnSortConfigurationPath,
  configOrderPath,
  configurationOrderPath,
  datasetUidPath,
  dataSourceTypePath,
  dimensionColumnPath,
  filtersPath,
  hasAggregationPath,
  headerSortCycleIndexPath,
  pivotColumnOrderPath,
  pivotColumnWidthsPath,
  pivotModePath,
  rowGroupCountPath,
  searchStringPath,
} from './helpers/AgTableObjectPathHelpers';
import { tableHasAggregation } from './helpers/TableDataColumnHelpers';

$.fn.socrataAgTable = socrataAgTable;
export default function socrataAgTable(originalVif: Vif, options: VisualizationOptions): SocrataAgTable {
  originalVif = migrateVif(cloneDeep(originalVif));

  const $element = $(this);
  const pagingEnabled = get(options, 'pagingEnabled', true);
  let visualization = $element.data('visualization');

  // Holds all state regarding the table's visual presentation.
  // Do _NOT_ update this directly, use setState() or updateState().
  // This is to ensure all state changes are reflected in the UI.
  // NOTE: Avoid adding things to this object!
  let renderState = $element.data('renderState') || {
    vif: null,
    // Is the table busy?
    busy: false,
    // Did we freak out somewhere trying to get data?
    error: false,
    fetchingRowCount: false,
    collocating: false,
    width: null,
    lastOrderState: null,
  };

  const getExportData = async (selectedFiltered: boolean) => {
    const visualizationVif = visualization.getVif();
    const activeHierarchyId = getActiveHierarchyId(visualizationVif);
    const { hierarchyIndex: activeHierarchyIndex } = findHierarchyInfo(visualizationVif, activeHierarchyId);
    const configurationOrder = get(visualizationVif, configurationOrderPath(activeHierarchyIndex));
    const data = await setExportDataQuery(visualizationVif, configurationOrder, selectedFiltered);
    return data;
  };

  function getVisualizationDataSource() {
    const datasource: IServerSideDatasource = {
      getRows: async (params: IServerSideGetRowsParams) => {
        const { request, api, context } = params;
        const { hierarchyConfig } = context as CustomAgGridContext;
        const customPivotOrder = hierarchyConfig?.pivotColumnOrder;
        const configurationOrder = hierarchyConfig?.order;
        let { startRow } = request;
        const { endRow } = request;
        // Ag-grids default page is 100 but we need more data for pivot
        let pageSize = isPivotInAgRequest(request) ? 1000 : 100;
        if (!startRow) {
          startRow = 0;
        }
        if (startRow && endRow) {
          pageSize = endRow - startRow;
        }
        const searchString = get(visualization.getVif(), searchStringPath);

        /* If we have multiple hierarchies, each one may have a different order.
         * We also load all hierarchies, including inactive ones, as soon as the page loads.
         * So we can't reference the hierarchy from the VIF directly, using activeHierarchyId.
         * Instead, each grid sets its hierarchy on the custom context object, and thats what we ref here.
         */

        try {
          const datasetRows = await setDataQuery(
            visualization.getVif(),
            startRow,
            pageSize,
            configurationOrder,
            request,
            searchString,
          );

          // Determine if we need to log to the lookup tool usage.
          const { lookupLogCallback } = options;
          if (lookupLogCallback !== undefined && typeof lookupLogCallback === 'function') {
            const filters = get(visualization.getVif(), filtersPath, []);
            const { rowGroupCols } = request;
            lookupLogCallback(filters, rowGroupCols, datasetRows?.datasetRowCount);
          }

          // If we're not pivoting, just return normally
          if (!isPivotInAgRequest(request)) {
            if (isPivotFeatureFlagOn()) {
              // Clear any PivotFields we may have set when pivotMode was toggled on
              clearPivotResultCols(api);
            }
            return params.success({
              rowData: datasetRows?.rows,
              rowCount: datasetRows?.datasetRowCount,
            });
          }

          const { pivotedRows, pivotFields } = doClientSidePivot(
            request,
            datasetRows?.rows,
            customPivotOrder,
          );

          const datasetUid = get(visualization.getVif(), datasetUidPath, '');

          addPivotResultCols(request, pivotFields, api, datasetUid);

          // Apply stored column widths after setting pivot columns
          if (hierarchyConfig?.pivotColumnWidths) {
            applyPivotColumnWidths(api, hierarchyConfig.pivotColumnWidths);
          }

          return params.success({
            rowData: pivotedRows,
            rowCount: pivotedRows.length,
          });
        } catch (e) {
          return;
        }
      },
    };
    return datasource;
  }

  async function fetchUniqueValues(
    vif: Vif,
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ): Promise<UniqueValuesData> {
    try {
      const data = await setAgUniqueValuesQuery({ vif, columns, order, customSortConfig });
      return data;
    } catch (error) {
      handleSetDataQueryError(error);
      return {
        rows: [],
      };
    }
  }

  async function getUniqueValues(
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
  ): Promise<UniqueValuesData> {
    const data = await fetchUniqueValues(visualization.getVif(), columns, order, customSortConfig);
    return data;
  }

  function initialize() {
    if (visualization) {
      return;
    }

    $element.addClass('socrata-paginated-table');

    // Get the current width, store that in state,
    // and use that to determine if the viz needs to be rendered with special small CSS
    const width = parseInt($element.css('width').slice(0, -2));
    updateState({ width: width });

    visualization = new AgTable($element, originalVif, {
      ...options,
      datasource: getVisualizationDataSource(),
      handleGetGrandTotalRow,
      handleColumnPivotChange,
      handleColumnPivotModeChange,
      handleColumnReorder,
      handleColumnResize,
      handleColumnRowGroupChange,
      handleColumnSort,
      handleColumnValueChange,
      handleColumnVisibilityChange,
      handleHierarchyTabChange,
      handleFilterChange,
      /**
       * `getExportData`'s return type for `columns` is `null[]`, which is probably just due to
       * that it's JS. Cast the value here to `any`, but the `key` here is actually still the right
       * type.
       */
      getExportData: getExportData as any,
      getUniqueValues,
      handleCustomSortCycle,
      handleIndentedHeaderSortIndex,
    });
    $element.data('visualization', visualization);

    visualization.render(originalVif);
  }

  function teardown() {
    visualization.destroy();
    $element.removeData('visualization');
    $element.removeData('renderState');
    detachEvents();
  }

  function emitVifChange(nextVif: Vif, agFilterModel?: AgColumnFilter) {
    visualization.emitVifEvent(nextVif);

    if (options.onAgTableVifUpdate) {
      options.onAgTableVifUpdate(nextVif, agFilterModel);
    }
  }

  function handleLoading(isLoading: boolean) {
    if (isLoading) {
      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_START');
      visualization.showBusyIndicator();
    } else {
      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_COMPLETE');
      visualization.hideBusyIndicator();
    }
  }

  function attachEvents() {
    detachEvents();

    $element.on('SOCRATA_VISUALIZATION_DESTROY.socrata-table', teardown);

    $element.on('SOCRATA_VISUALIZATION_RENDER_VIF.socrata-table', handleRenderVif);

    $element.on('SOCRATA_VISUALIZATION_INVALIDATE_SIZE.socrata-table', handleComponentResize);
    eventBus.on('VISUALIZATION_LOADING', handleLoading);
  }

  function detachEvents() {
    $element.off('.socrata-table');
    eventBus.remove('VISUALIZATION_LOADING', handleLoading);
  }

  function render(nextVif?: Vif) {
    const { error, collocating, width } = renderState;

    if (collocating) {
      return visualization.renderCollocationMessage();
    }

    if (error) {
      return visualization.renderError();
    }

    visualization.hideBusyIndicator();
    visualization.renderComponentSize(width);

    if (nextVif) {
      visualization.render(nextVif);
    }
  }

  // Event handler functions
  async function handleRenderVif(e: JQuery.Event) {
    const event = e as VifEvent;
    const nextVif = migrateVif(cloneDeep(event.originalEvent?.detail));

    updateState({}, nextVif);
  }

  function handleHierarchyTabChange(hierarchyId: any) {
    const visualizationVif = visualization.getVif();
    const currentHierarchy = getActiveHierarchyId(visualizationVif);
    if (currentHierarchy !== hierarchyId) {
      const nextVif = cloneDeep(visualizationVif);
      set(nextVif, activeHierarchyIdPath, hierarchyId);
      updateState({}, nextVif);
      emitVifChange(nextVif);
    }
  }

  /**
   * triggered from onFilterChange in common/visualizations/views/AgTable.tsx
   * @param newFilters the new vif filters we've changed to
   * @param agFilterModel optionally, the agGrid data object representing the filters
   */
  function handleFilterChange(newFilters: Filters, agFilterModel?: AgColumnFilter) {
    const nextVif = cloneDeep(visualization.getVif());
    set(nextVif, filtersPath, newFilters);
    emitVifChange(nextVif, agFilterModel);
  }

  function handleColumnReorder(columnState: ColumnState[]) {
    //  Handle pivot column reordering if in pivot mode
    const { hierarchyConfig, activeHierarchyIndex } = getCurrentHierarchyInfo(visualization);

    if (hierarchyConfig && isPivotInHierarchy(hierarchyConfig)) {
      const uniquePivotOrder = extractPivotColumnOrder(columnState);
      const currentOrder = get(visualization.getVif(), pivotColumnOrderPath(activeHierarchyIndex), []);
      if (uniquePivotOrder.length > 0 && !isEqual(uniquePivotOrder, currentOrder)) {
        const nextVif = cloneDeep(visualization.getVif());
        set(nextVif, pivotColumnOrderPath(activeHierarchyIndex), uniquePivotOrder);

        updateState({}, nextVif);
        emitVifChange(nextVif);

        return; // Early return to avoid double VIF updates
      }
    }
    // Not pivot mode: continue with normal column reordering
    const columnsFromVif = get(visualization.getVif(), dimensionColumnPath, []);

    const updatedColumnState = columnState.reduce((updatedColumns, columnFromGrid) => {
      const columnFromVif = columnsFromVif.find(
        (vifColumn: { fieldName: string | undefined }) => vifColumn.fieldName === columnFromGrid.colId,
      );

      // the column is no longer in the vif, so we will not try to add it back
      if (!columnFromVif) {
        return updatedColumns;
      }

      return updatedColumns.concat(columnFromVif);
    }, []);

    const newUpdatedColumns = compact(updatedColumnState);

    if (!isEqual(columnsFromVif, newUpdatedColumns)) {
      const nextVif = cloneDeep(visualization.getVif());

      set(nextVif, dimensionColumnPath, newUpdatedColumns);

      updateState({}, nextVif);
      emitVifChange(nextVif);
    }
  }

  function handleColumnResize(columns: Column[] | null) {
    if (columns) {
      // Handle pivot column width changes if in pivot mode
      const { hierarchyConfig, activeHierarchyIndex } = getCurrentHierarchyInfo(visualization);

      if (hierarchyConfig && isPivotInHierarchy(hierarchyConfig)) {
        const pivotColumnWidths = extractPivotColumnWidths(columns);

        if (Object.keys(pivotColumnWidths).length > 0) {
          const nextVifForPivot = cloneDeep(visualization.getVif());
          // Merge with existing pivot column widths
          const existingWidths = hierarchyConfig.pivotColumnWidths || {};
          const mergedWidths = { ...existingWidths, ...pivotColumnWidths };

          set(nextVifForPivot, pivotColumnWidthsPath(activeHierarchyIndex), mergedWidths);
          updateState({}, nextVifForPivot);
          emitVifChange(nextVifForPivot);
          return; // Early return to avoid double VIF updates
        }
      }

      //Not pivot mode: continue with normal column resizing

      // There are three cases we need to handle:
      // 1. Individual columns being resized - column.getColDef().field is defined and its value is column's fieldName
      // 2. Grouped columns being resized: column.getColDef().field is undefined
      //    a. isIndented is true and AG Grid uses a single column for grouped columns - column.getColId() is
      //       set to GROUP_COLUMN_PREFIX
      //    b. isIndented is false and AG Grid uses multiple columns for grouped columns - column.getColId() is
      //       set to "{GROUP_COLUMN_PREFIX}-{column's fieldName}"

      const nextVif: Vif = cloneDeep(visualization.getVif());
      columns.forEach((column) => {
        // We want to handle case 2a first, because we set the width in the hierarchy configuration.
        // For 1 and 2b, we set it in dimension.columns array.
        const colId = column.getColId();
        if (colId === GROUP_COLUMN_PREFIX) {
          // case 2a
          // If the single auto column is resized, we want to store the width in hierarchy configuration
          // This allows us to keep original width for individual columns, so that columns that are being
          // removed from grouping can be resized back to their original width.
          const activeHierarchyId = nextVif.series[0].dataSource.activeHierarchyId;
          const affectedHierarchy = nextVif.series[0].dataSource.hierarchies?.find(
            (h) => h.id === activeHierarchyId,
          );
          if (affectedHierarchy) {
            affectedHierarchy.singleAutoColumnWidth = column.getActualWidth();
          }
        } else {
          // field is only defined for case 1.
          let fieldName = column.getColDef().field;

          if (!fieldName) {
            // case 2b
            // colId is in the format "{GROUP_COLUMN_PREFIX}-{column's fieldName}" - extract column's fieldName
            fieldName = colId.split(`${GROUP_COLUMN_PREFIX}-`, 2)[1];
          }

          const cols: ViewColumn[] | undefined = nextVif.series[0].dataSource.dimension?.columns;
          const affectedColumn = cols?.find((c) => c.fieldName === fieldName);
          if (affectedColumn) {
            affectedColumn.width = column.getActualWidth();
          }
        }
      });
      updateState({}, nextVif);
      emitVifChange(nextVif);
    }
  }

  async function handleGetGrandTotalRow(hierarchyId: string) {
    const hierarchyConfig = getCurrentHierarchyInfo(visualization).hierarchies.find(
      (h) => h.id === hierarchyId,
    );
    if (!hierarchyConfig) {
      return [];
    }
    try {
      const grandTotalRows = await setGrandTotalDataQuery(
        visualization.getVif(),
        hierarchyConfig,
        visualization.shouldFetchAggregationSuggestions(),
      );
      return grandTotalRows;
    } catch (e) {
      return [];
    }
  }

  /**
   * triggered from onColumnRowGroupChange in common/visualizations/views/AgTable.tsx
   * @param columnState agGridColumn state
   * @param columns AG Grid Columns that changed
   * @param hierarchyId The hierarchy that received the column row group change
   */
  function handleColumnRowGroupChange(columnState: ColumnState[], columns: Column[], hierarchyId?: string) {
    if (!hierarchyId) {
      // This is an ungrouped table.
      return;
    }

    const nextVif = cloneDeep(visualization.getVif());
    const hierarchies = getTableHierarchies(visualization.getVif()) ?? [];
    const hierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id === hierarchyId);
    const hierarchyColumnConfigurations = hierarchies[hierarchyIndex].columnConfigurations;
    const newHierarchyColumnConfigurations = cloneDeep(hierarchyColumnConfigurations);

    // For each of the columns in the table, find its corresponding hierarchy definition and update it.
    columns.forEach((col) => {
      const columnConfigIdx = findIndex(
        hierarchyColumnConfigurations,
        (columnConfig) => columnConfig.columnName === col.getColDef().field,
      );

      if (columnConfigIdx >= 0) {
        const newConfig: HierarchyColumnConfig = {
          columnName: col.getColDef().field ?? '',
          aggregation: col.isValueActive() ? ((col.getAggFunc() as string) ?? null) : null,
          isGrouping: col.isRowGroupActive(),
          hidden: !col.isVisible(),
        };

        // We can't set netTotalCalc: undefined here, because it
        // would trigger an isEqual(oldVif, newVif) in StoryVisualization
        const maybeNetTotalCalc = hierarchyColumnConfigurations[columnConfigIdx].netTotalCalc;
        if (maybeNetTotalCalc) {
          newConfig.netTotalCalc = maybeNetTotalCalc;
        }

        if (
          isTableAdvancedTotalingEnabled() &&
          'customFormula' in hierarchyColumnConfigurations[columnConfigIdx]
        ) {
          newConfig.customFormula = hierarchyColumnConfigurations[columnConfigIdx].customFormula;
        }

        newHierarchyColumnConfigurations[columnConfigIdx] = newConfig;
      }
    });

    const finalHierarchColumnConfigs = sortHierarchyColumnConfigurations(
      newHierarchyColumnConfigurations,
      columnState,
    );
    if (isTableAdvancedTotalingEnabled()) {
      const hasAggregation = tableHasAggregation(finalHierarchColumnConfigs);
      const rowGroupCount = finalHierarchColumnConfigs.filter(
        (columnConfig) => columnConfig.isGrouping,
      ).length;
      set(nextVif, hasAggregationPath(hierarchyIndex), hasAggregation);
      set(nextVif, rowGroupCountPath(hierarchyIndex), rowGroupCount);
    }
    set(nextVif, columnConfigurationPath(hierarchyIndex), finalHierarchColumnConfigs);

    updateState({}, nextVif);
    emitVifChange(nextVif);
  }

  /**
   * triggered from onColumnValueChange in common/visualizations/views/AgTable.tsx
   * @param columns AG Grid Columns that changed
   * @param hierarchyId The hierarchy that received the column row group change
   */
  function handleColumnValueChange(columns: Column[], hierarchyId?: string) {
    if (!hierarchyId) {
      // This is an ungrouped table.
      return;
    }

    const nextVif = cloneDeep(visualization.getVif());
    const hierarchies = getTableHierarchies(visualization.getVif()) ?? [];
    const hierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id === hierarchyId);
    const hierarchyColumnConfigurations = hierarchies[hierarchyIndex].columnConfigurations;
    columns.forEach((col) => {
      const newConfig: HierarchyColumnConfig = {
        columnName: col.getColDef().field ?? '',
        aggregation: col.isValueActive() ? ((col.getAggFunc() as string) ?? null) : null,
        isGrouping: col.isRowGroupActive(),
        hidden: !col.isVisible(),
      };
      const columnConfigIdx = findIndex(
        hierarchyColumnConfigurations,
        (columnConfig) => columnConfig.columnName === col.getColDef().field,
      );

      if (columnConfigIdx >= 0) {
        set(nextVif, `${columnConfigurationPath(hierarchyIndex)}[${columnConfigIdx}]`, newConfig);
      } else {
        // There was no hierarchy column config for this column. Add a new one with the aggregation.
        const newHierarchyColumnConfigurations = get(nextVif, columnConfigurationPath(hierarchyIndex), []);
        newHierarchyColumnConfigurations.push(newConfig);
        set(nextVif, columnConfigurationPath(hierarchyIndex), newHierarchyColumnConfigurations);
      }
    });
    const newColumnConfigs = get(nextVif, columnConfigurationPath(hierarchyIndex)) as HierarchyColumnConfig[];
    if (isTableAdvancedTotalingEnabled()) {
      const hasAggregation = tableHasAggregation(newColumnConfigs);
      set(nextVif, hasAggregationPath(hierarchyIndex), hasAggregation);
    }

    updateState({}, nextVif);
    emitVifChange(nextVif);
  }

  /**
   * triggered from onColumnPivotChange in common/visualizations/views/AgTable.tsx
   * @param columnState agGridColumn state
   * @param columns AG Grid Columns that changed
   * @param hierarchyId The hierarchy that received the pivot column change
   */
  function handleColumnPivotChange(columnState: ColumnState[], columns: Column[], hierarchyId?: string) {
    if (!hierarchyId) {
      // This is an ungrouped table.
      return;
    }

    const nextVif = cloneDeep(visualization.getVif());
    const hierarchies = getTableHierarchies(visualization.getVif()) ?? [];
    const hierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id === hierarchyId);
    const hierarchyColumnConfigurations = hierarchies[hierarchyIndex].columnConfigurations;
    const newHierarchyColumnConfigurations = cloneDeep(hierarchyColumnConfigurations);

    // For each of the columns in the table, find its corresponding hierarchy definition and update it.
    columns.forEach((col) => {
      const newConfig: HierarchyColumnConfig = {
        columnName: col.getColDef().field ?? '',
        aggregation: col.isValueActive() ? ((col.getAggFunc() as string) ?? null) : null,
        isGrouping: false, // Can't be a pivot col and a group col at the same time
        hidden: !col.isVisible(),
        isPivoting: col.isPivotActive(),
      };
      const columnConfigIdx = findIndex(
        hierarchyColumnConfigurations,
        (columnConfig) => columnConfig.columnName === col.getColDef().field,
      );

      if (columnConfigIdx >= 0) {
        newHierarchyColumnConfigurations[columnConfigIdx] = newConfig;
      } else {
        // There was no hierarchy column config for this column. Add a new one.
        newHierarchyColumnConfigurations.push(newConfig);
      }
    });

    const finalHierarchColumnConfigs = sortHierarchyColumnConfigurations(
      newHierarchyColumnConfigurations,
      columnState,
    );

    set(nextVif, columnConfigurationPath(hierarchyIndex), finalHierarchColumnConfigs);

    // Remove sorting for any columns that are not row group columns
    const hierarchy = nextVif.series[0].dataSource.hierarchies[hierarchyIndex];
    const newOrder = removeSortingFromNonRowGroupColumns(hierarchy);
    set(nextVif, configurationOrderPath(hierarchyIndex), newOrder);

    updateState({}, nextVif);
    emitVifChange(nextVif);
  }

  /**
   * triggered from onColumnPivotModeChange in common/visualizations/views/AgTable.tsx
   * @param pivotMode pivot mode new setting
   * @param hierarchyId The hierarchy that received the pivot mode change
   * Toggles the value of pivotMode
   */
  function handleColumnPivotModeChange(pivotMode: boolean, hierarchyId?: string) {
    if (!hierarchyId) {
      // This is an ungrouped table.
      return;
    }
    const nextVif = cloneDeep(visualization.getVif());
    const hierarchies = getTableHierarchies(visualization.getVif()) ?? [];
    const hierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id === hierarchyId);

    set(nextVif, pivotModePath(hierarchyIndex), pivotMode);
    const newHierarchy = nextVif.series[0].dataSource.hierarchies[hierarchyIndex];

    if (isPivotInHierarchy(newHierarchy)) {
      // If pivot mode is on, remove sorting for any columns that are not row group columns
      const newOrder = removeSortingFromNonRowGroupColumns(hierarchies[hierarchyIndex]);
      set(nextVif, configurationOrderPath(hierarchyIndex), newOrder);
    }

    updateState({}, nextVif);
    emitVifChange(nextVif);
  }

  const handleIndentedHeaderSortIndex = (headerSortCycleIndex: number, hierarchyId?: string) => {
    if (!isCustomSortEnabled()) {
      return;
    }

    const visualizationVif = visualization.getVif();
    const activeHierarchyId = getActiveHierarchyId(visualizationVif);
    const { hierarchies, hierarchyIndex: activeHierarchyIndex } = findHierarchyInfo(
      visualizationVif,
      activeHierarchyId,
    );

    if (hierarchies.length === 0 || activeHierarchyId !== hierarchyId) {
      return;
    }
    const nextVif = cloneDeep(visualizationVif);

    set(nextVif, headerSortCycleIndexPath(activeHierarchyIndex), headerSortCycleIndex);

    updateState({}, nextVif);
    emitVifChange(nextVif);
  };

  const handleCustomSortCycle = (newOrderConfig: OrderConfig[], hierarchyId?: string) => {
    const nonNullOrderConfig = newOrderConfig.filter(
      (conf) => !has(conf, 'sortToFollow') || !isNil(conf.sortToFollow),
    );

    const visualizationVif = visualization.getVif();
    const dataSourceType = get(visualizationVif, dataSourceTypePath);

    const activeHierarchyId = getActiveHierarchyId(visualizationVif);
    const { hierarchies, hierarchyIndex: activeHierarchyIndex } = findHierarchyInfo(
      visualizationVif,
      activeHierarchyId,
    );

    if (dataSourceType !== 'socrata.soql' && dataSourceType !== 'socrata.rawSoql') {
      return;
    }

    if (renderState.busy) {
      return;
    }
    if (hierarchies.length === 0) {
      const existingOrder = get(visualizationVif, configOrderPath);
      if (!isEqual(existingOrder, nonNullOrderConfig)) {
        const nextVif = cloneDeep(visualizationVif);

        set(nextVif, configOrderPath, nonNullOrderConfig);

        updateState({ lastOrderState: existingOrder }, nextVif);
        emitVifChange(nextVif);
      }
    } else {
      if (activeHierarchyId !== hierarchyId) {
        return;
      }

      const existingHierarchyOrder = hierarchies[activeHierarchyIndex].order || [];
      if (!isEqual(existingHierarchyOrder, nonNullOrderConfig)) {
        const nextVif = cloneDeep(visualizationVif);

        set(nextVif, configurationOrderPath(activeHierarchyIndex), nonNullOrderConfig);

        updateState({}, nextVif);
        emitVifChange(nextVif);
      }
    }
  };

  /**
   * triggered from onColumnSort in common/visualizations/views/AgTable.tsx
   * @param columnState contains the single column with non-null "sort" value
   * @param hierarchyId The hierarchy that received the column row group change
   */
  function handleColumnSort(columnState: ColumnState[], hierarchyId?: string) {
    const visualizationVif = visualization.getVif();
    const dataSourceType = get(visualizationVif, dataSourceTypePath);

    const activeHierarchyId = getActiveHierarchyId(visualizationVif);
    const { hierarchies, hierarchyIndex: activeHierarchyIndex } = findHierarchyInfo(
      visualizationVif,
      activeHierarchyId,
    );

    if (dataSourceType !== 'socrata.soql' && dataSourceType !== 'socrata.rawSoql') {
      return;
    }

    if (renderState.busy) {
      return;
    }

    // based on column state data, select only sorted columns
    const sortedColumns: ColumnState[] = columnState.filter((column) => column.sort !== null);
    // .sort mutates the array
    // This sorts the columns based on the sortIndex, organizing the columns in the multi sort order.
    sortedColumns.sort((colA: ColumnState, colB: ColumnState) => {
      const aSortIndex = colA?.sortIndex ?? 0;
      const bSortIndex = colB?.sortIndex ?? 0;

      return aSortIndex - bSortIndex;
    });

    const firstRowGroup = columnState.filter((column) => column.rowGroupIndex == 0)[0];

    // restructure to a vif-friendly format
    const vifFriendlyOrder = sortedColumns.map(({ colId, sort }) => ({
      columnName:
        colId === GROUP_COLUMN_PREFIX && !!firstRowGroup
          ? `${GROUP_COLUMN_PREFIX}-${firstRowGroup.colId}`
          : colId,
      ascending: sort === 'asc',
    }));

    // put the order onto the vif in one of two formats:
    // 1. as global order when there are no hierarchies created
    // 2. as localized order specific to active hierarchy
    if (hierarchies.length === 0) {
      const existingOrder = get(visualizationVif, configOrderPath);
      if (!isEqual(existingOrder, vifFriendlyOrder)) {
        const nextVif = cloneDeep(visualizationVif);

        set(nextVif, configOrderPath, vifFriendlyOrder);

        updateState({ lastOrderState: existingOrder }, nextVif);
        emitVifChange(nextVif);
      }
    } else {
      if (activeHierarchyId !== hierarchyId) {
        return;
      }

      const existingHierarchyOrder = hierarchies[activeHierarchyIndex].order || [];
      if (!isEqual(existingHierarchyOrder, vifFriendlyOrder)) {
        const nextVif = cloneDeep(visualizationVif);
        set(nextVif, configurationOrderPath(activeHierarchyIndex), vifFriendlyOrder);

        updateState({}, nextVif);
        emitVifChange(nextVif);
      }
    }
  }

  /**
   * triggered from onColumnVisibilityChange in common/visualizations/views/AgTable.tsx
   * @param columnsState agGridColumn state, which includes the hide state for each column
   * @param columns AG Grid Columns
   * @param hierarchyId The hierarchy that received the column visibility change
   */
  function handleColumnVisibilityChange(
    columnsState: ColumnState[],
    columns: Column[] | null,
    hierarchyId?: string,
  ) {
    const visualizationVif = visualization.getVif();
    const activeHierarchyId = getActiveHierarchyId(visualizationVif);
    const { hierarchies, hierarchyIndex: activeHierarchyIndex } = findHierarchyInfo(
      visualizationVif,
      activeHierarchyId,
    );
    const nextVif = cloneDeep(visualizationVif);

    if (hierarchies.length !== 0 && columns) {
      if (activeHierarchyId === hierarchyId) {
        // Note: This list might not include all columns in the table
        const hierarchyColumnConfigurations: HierarchyColumnConfig[] = get(
          nextVif,
          columnConfigurationPath(activeHierarchyIndex),
        );

        // For each of the columns in the table, find its corresponding hierarchy definition
        // and update its visibility.
        columns.forEach((column) => {
          const columnName = column.getColDef().field!;

          const columnConfigIdx = findIndex(
            hierarchyColumnConfigurations,
            (columnConfig) => columnConfig.columnName === columnName,
          );

          if (columnConfigIdx >= 0) {
            const isGrouping = get(hierarchyColumnConfigurations[columnConfigIdx], 'isGrouping', false);
            set(
              nextVif,
              `${columnConfigurationPath(activeHierarchyIndex)}[${columnConfigIdx}].hidden`,
              // We hide grouping columns from AG Grid's columns overlay,
              // so a grouping column will have column.isVisible() == true
              !isGrouping && !column.isVisible(),
            );
          } else {
            // There was no hierarchy column config for this column. Add a new one with the column visibility.
            hierarchyColumnConfigurations.push({
              columnName,
              aggregation: null,
              isGrouping: false,
              hidden: !column.isVisible(),
            });
            set(nextVif, columnConfigurationPath(activeHierarchyIndex), hierarchyColumnConfigurations);
          }
        });
      }
    } else if (columns) {
      // When there are no hierarchies defined, set the visibility on the columns directly.
      columns.forEach((column) => {
        const columnName = column.getColDef().field!;
        const affectedColumn = find(get(nextVif, dimensionColumnPath), {
          fieldName: columnName,
        }) as ViewColumn | undefined;
        if (affectedColumn) {
          affectedColumn.hide = !column.isVisible();
        }
      });
    }
    updateState({}, nextVif);
    emitVifChange(nextVif);
  }

  /**
   * If the component is resized in storyteller, update the state with the new width,
   * which is sued to determine if the viz should be rendered with special small CSS
   */
  function handleComponentResize() {
    const renderStateWidth = renderState.width;
    const width = parseInt($element.css('width').slice(0, -2));

    if (!isEqual(renderStateWidth, width)) {
      updateState({ width: width });
    }
  }

  /**
   * Data Requests
   */

  function handleSetDataQueryError(error: any) {
    if (window.console && isFunction(window.console.error)) {
      console.error('Error while fulfilling table data request:', error.soqlError ?? error.message ?? error);
      console.error('stack:', error.stack);
    }

    const collocating = get(error, 'soqlError.status') === 'in-progress';

    // There was an issue populating this table with data. Retry?
    updateState({ busy: false, error: true, collocating });

    return Promise.reject(`Error while fulfilling table data request: ${JSON.stringify(error)}`);
  }

  const setExportDataQuery = async (vif: Vif, order: OrderConfig[], selectedFiltered: boolean) => {
    try {
      visualization.showBusyIndicator();
      updateState({ busy: true });
      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_START');
      const previousOrder = renderState.lastOrderState;
      const data = await setAgSoqlExportQuery(vif, order, previousOrder, selectedFiltered);
      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_COMPLETE');
      return data;
    } catch (error) {
      return {
        columns: [],
        rows: [],
        rowIds: [],
      };
    } finally {
      visualization.hideBusyIndicator();
      updateState({ busy: false });
    }
  };

  async function setDataQuery(
    vifForDataQuery: Vif,
    startIndex: number,
    pageSize: number,
    order: OrderConfig[] | undefined,
    agRequest: any,
    searchString: string,
  ) {
    try {
      visualization.showBusyIndicator();
      updateState({ busy: true });

      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_START');

      const {
        rows,
        vif: nextVif,
        datasetRowCount,
        grandTotal,
      } = (await initiateDataQuery({
        agRequest,
        order,
        pageSize,
        searchString,
        startIndex,
        vifForDataQuery,
        /**
         * `initiateDataQuery` does not return `rows` or `grandTotal` in all paths.
         * Casting to `any` as a work around.
         */
      })) as any;

      $element.trigger('SOCRATA_VISUALIZATION_DATA_LOAD_COMPLETE');
      updateState({ busy: false, fetchingRowCount: false }, nextVif);

      return { datasetRowCount, rows, grandTotal };
    } catch (error) {
      await handleSetDataQueryError(error);
    } finally {
      visualization.hideBusyIndicator();
    }
  }

  function initiateDataQuery({
    agRequest,
    order,
    pageSize,
    searchString,
    startIndex,
    vifForDataQuery,
  }: {
    agRequest: IServerSideGetRowsRequest;
    order?: OrderConfig[];
    pageSize: number;
    searchString: string;
    startIndex: number;
    vifForDataQuery: Vif;
  }) {
    // Check if apply-all-filters feature is enabled and report hasn't been run
    const applyAllFiltersEnabled = FeatureFlags.value('enable_batch_running_filters');
    // if we are not in the context of storyteller we want to render the viz by default
    const hasReportBeenRun = visualization.getOptions()?.hasReportBeenRun ?? true;

    // Don't execute queries if apply-all-filters is enabled and report hasn't been run
    if (applyAllFiltersEnabled && !hasReportBeenRun) {
      return Promise.resolve({ rows: [], datasetRowCount: 0 });
    }

    const dataSourceType = get(vifForDataQuery, dataSourceTypePath);
    switch (dataSourceType) {
      case 'socrata.inline':
        return setInlineDataQuery(vifForDataQuery, null, startIndex, pageSize, pagingEnabled);

      case 'socrata.soql':
        // This can update the VIF with an order
        return setAgSoqlDataQuery({
          vifForDataQuery,
          startIndex,
          pageSize,
          order,
          agRequest,
          searchString,
          previousOrder: renderState.lastOrderState,
          shouldFetchAggregationSuggestions: visualization.shouldFetchAggregationSuggestions(),
        });

      case 'socrata.rawSoql':
        return setRawSoqlDataQuery(vifForDataQuery, null, startIndex, pageSize, order);

      case 'socrata.view':
        return setSocrataViewDataQuery(vifForDataQuery, null);

      default:
        return Promise.reject(`Invalid data source type in vif: '${dataSourceType}'.`);
    }
  }

  // Updates only specified UI state.
  function updateState(newPartialState: Partial<AgTableRenderState>, nextVif?: Vif) {
    setState(extend({}, renderState, newPartialState), nextVif);
  }

  // Replaces entire UI state.
  function setState(newState: AgTableRenderState, nextVif?: Vif) {
    const vifChanged = nextVif && !isEqual(visualization.getVif(), nextVif);
    const dataChanged =
      nextVif && !isEqual(getParameterOverrides(visualization.getVif()), getParameterOverrides(nextVif));

    let customSortConfigChanged = false;
    if (isCustomSortEnabled() && !isUndefined(visualization)) {
      const currentHierarchyIndex = getCurrentHierarchyIndex(nextVif);
      const oldHierarchyIndex = getCurrentHierarchyIndex(visualization.getVif());
      const newCustomSortConfig = get(nextVif, columnSortConfigurationPath(currentHierarchyIndex));
      const oldCustomSortConfig = get(visualization.getVif(), columnSortConfigurationPath(oldHierarchyIndex));

      customSortConfigChanged = !!vifChanged && !isEqual(newCustomSortConfig, oldCustomSortConfig);
    }

    if (customSortConfigChanged || dataChanged) {
      renderState = newState;

      // Update dataSource with new vif state that include parameterOverrides
      const newDataSource = getVisualizationDataSource();
      visualization.updateDataSource(newDataSource);
    }

    if (vifChanged || !isEqual(renderState, newState)) {
      const becameIdle = !newState.busy && renderState.busy;
      const newWidth = renderState.width ? !isEqual(renderState.width, newState.width) : null;

      renderState = newState;

      if (becameIdle || vifChanged || newWidth) {
        render(nextVif);
      }
    }
  }

  initialize();
  attachEvents();

  return this;
}
